package com.rosario.juridico.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.*
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.ui.components.WebScrollableBox
import com.rosario.juridico.ui.components.WebLazyColumnScrollbar
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.material3.MaterialTheme.colorScheme
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.*
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.window.PopupProperties
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.material3.FilterChip
import androidx.compose.material3.SuggestionChip
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.navigation.NavController
import com.rosario.juridico.domain.AppMontos
import com.rosario.juridico.platform.BackendClient
import com.rosario.juridico.ui.theme.Gold400
import com.rosario.juridico.domain.*
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.premium.PremiumManager
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.domain.AccessState
import com.rosario.juridico.platform.ShareManager
import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.ui.platform.LocalClipboardManager
import com.rosario.juridico.domain.model.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.jetbrains.compose.resources.ExperimentalResourceApi
import juridico5.composeapp.generated.resources.Res
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.layout.imePadding
import androidx.compose.ui.graphics.graphicsLayer

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES GLOBAL DE REFERENCIA
// ─────────────────────────────────────────────────────────────────────────────
const val VALOR_JUS_ESTANDAR = AppMontos.JUS_CALCULADORA

// ─────────────────────────────────────────────────────────────────────────────
// MODELOS SERIALIZABLES (LEY 6767 JSON)
// ─────────────────────────────────────────────────────────────────────────────

@Serializable
data class Ley6767Data(
    val version_ley: String,
    val unidad_jus_base: Double,
    val procesosJudiciales: List<ProcesoJudicialJson>,
    val actuacionesExtrajudiciales: List<ActuacionExtrajudicialJson>
)

@Serializable
data class ProcesoJudicialJson(
    val id: String = "",
    val titulo: String = "",
    val articulo: String = "",
    val inciso: String? = null,
    val fuero: String = "civil",
    val requiereMonto: Boolean = false,
    val multiplicadorBase: Double? = null,
    val reduccionEscala: Double = 1.0,
    val minimoJus: Double = 0.0,
    val maximoJus: Double? = null,
    val descripcionBase: String? = null,
    val codigosSelladoSugeridos: List<String> = emptyList(),
    val sinonimos: List<String> = emptyList(),
    val permiteQuerellante: Boolean = false,
    val competencia: String? = null,
    val viaIngreso: String? = null,
    val mediacion: String? = null
)

@Serializable
data class ActuacionExtrajudicialJson(
    val id: String = "",
    val titulo: String = "",
    val articulo: String = "",
    val inciso: String? = null,
    val requiereMonto: Boolean = false,
    val porcentajeAlicuota: Double? = null,
    val reduccionEscala: Double = 1.0,
    val minimoJus: Double = 0.0,
    val descripcionBase: String? = null,
    val codigosSelladoSugeridos: List<String> = emptyList(),
    val sinonimos: List<String> = emptyList(),
    val permiteQuerellante: Boolean = false,
    val competencia: String? = null,
    val viaIngreso: String? = null,
    val mediacion: String? = null
)

data class TipoProceso(
    val nombre: String,
    val clave: String,
    val etapas: List<Etapa>,
    val reduccion: Double = 1.0,
    val esMinimo: Boolean = false,
    val minimoJus: Double = 0.0,
    val maximoJus: Double? = null,
    val isRange: Boolean = false,
    val fuero: String = "civil",
    val multiplicadorBase: Double? = null,
    val nota: String = "",
    val requiereMonto: Boolean = true,
    val codigosSelladoSugeridos: List<String> = emptyList(),
    val articulo: String = "",
    val inciso: String? = null,
    val permiteQuerellante: Boolean = false,
    val sinonimos: List<String> = emptyList(),
    val competencia: String? = null,
    val viaIngreso: String? = null,
    val mediacion: String? = null
)

data class ActuacionExtrajudicial(
    val nombre: String,
    val clave: String,
    val tipo: TipoRegulacionExtrajudicial,
    val minimoJus: Double,
    val maximoJus: Double? = null,
    val isRange: Boolean = false,
    val porcentaje: Double = 0.0,
    val mensaje: String,
    val codigosSelladoSugeridos: List<String> = emptyList(),
    val articulo: String = "",
    val inciso: String? = null,
    val requiereMonto: Boolean = false,
    val sinonimos: List<String> = emptyList(),
    val competencia: String? = null,
    val viaIngreso: String? = null,
    val mediacion: String? = null
)

data class Etapa(val nombre: String, val clave: String, val factor: Double)

data class ResultadoHonorario(
    val minimoJus: Double, val maximoJus: Double, val efectivoJus: Double,
    val minimoPesos: Double, val maximoPesos: Double, val efectivoPesos: Double,
    val descripcion: String, val articulo: String
)

data class ResultadoMediacion(
    val honorarioBase: Double,
    val plusMixto: Double,
    val plusReuniones: Double,
    val aportesCaja: Double,
    val plusComediador: Double,
    val totalJus: Double,
    val totalPesos: Double,
    val factorAjuste: Double,
    val normativa: String,
    val labelResultado: String,
    val baseJusEscala: Double,
    val tasaAportes: Double,
    val esMixto: Boolean,
    val valorJus: Double
)

data class ValorJusHistorico(val fecha: String, val valor: String)

// ─────────────────────────────────────────────────────────────────────────────
// LEY 6767 - LÓGICA EXPANDIDA ALINEADA CON LA BASE DE DATOS
// ─────────────────────────────────────────────────────────────────────────────

object Ley6767 {
    val etapasCompletas  = listOf(Etapa("Demanda", "demanda", 0.33), Etapa("Prueba", "prueba", 0.33), Etapa("Alegatos", "alegatos", 0.34))
    val etapaUnica       = listOf(Etapa("Proceso completo", "completo", 1.0))

    fun porcentajesEscala(montoJus: Double): Pair<Double, Double> = when {
        montoJus <= 15.0  -> 0.22 to 0.33
        montoJus <= 45.0  -> 0.20 to 0.26
        montoJus <= 90.0  -> 0.18 to 0.24
        montoJus <= 150.0 -> 0.17 to 0.22
        montoJus <= 450.0 -> 0.15 to 0.20
        montoJus <= 750.0 -> 0.13 to 0.17
        else              -> 0.10 to 0.13
    }

    fun calcular(proceso: TipoProceso, etapa: Etapa, montoBase: Double, valorJus: Double, conQuerellante: Boolean = false): ResultadoHonorario {
        val baseCalculo = montoBase * (proceso.multiplicadorBase ?: 1.0)
        val montoJusTotal = if (valorJus > 0) baseCalculo / valorJus else 0.0
        val multQuerellante = if (conQuerellante && (proceso.fuero == "penal" || proceso.permiteQuerellante)) 1.5 else 1.0

        if (proceso.esMinimo && baseCalculo == 0.0) {
            val minJ = proceso.minimoJus
            val maxJ = proceso.maximoJus ?: (minJ * 10)
            return ResultadoHonorario(minJ * multQuerellante, maxJ * multQuerellante, minJ * multQuerellante, minJ * multQuerellante * valorJus, maxJ * multQuerellante * valorJus, minJ * multQuerellante * valorJus, proceso.nombre, "Art. 12/14/19 Ley 6767")
        }

        val (pMin, pMax) = porcentajesEscala(montoJusTotal)

        var minP = baseCalculo * pMin * proceso.reduccion * etapa.factor * multQuerellante
        var maxP = baseCalculo * pMax * proceso.reduccion * etapa.factor * multQuerellante
        var efP  = minP

        if (proceso.esMinimo && (efP / (if(valorJus>0) valorJus else 1.0)) < (proceso.minimoJus * multQuerellante)) {
            val minLegalPesos = proceso.minimoJus * multQuerellante * valorJus
            minP = minLegalPesos
            efP = minLegalPesos
        }

        val articulo = when {
            proceso.multiplicadorBase == 36.0 -> "Base 36 meses (Art. 8 d)"
            proceso.multiplicadorBase == 24.0 -> "Base 24 meses (Art. 4, 5, 6 y Jurisprudencia)"
            proceso.clave == "sucesorio_basico" -> "Art. 13 Ley 6767"
            proceso.fuero == "penal" -> "Art. 14 Ley 6767"
            proceso.reduccion == 0.80 -> "Art. 7 Ley 6767 (80%)"
            proceso.reduccion == 0.50 -> "Art. 7 / Art. 19 Ley 6767"
            proceso.reduccion == 0.30 -> "Art. 16 Ley 6767 (30%)"
            proceso.reduccion == 0.05 -> "Art. 7 (5%) Ley 6767"
            else -> "Art. 6 Ley 6767"
        }

        return ResultadoHonorario(
            minP / (if(valorJus>0) valorJus else 1.0),
            maxP / (if(valorJus>0) valorJus else 1.0),
            efP / (if(valorJus>0) valorJus else 1.0),
            minP, maxP, efP,
            "${proceso.nombre} — ${etapa.nombre}",
            articulo
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// LÓGICA DE HONORARIOS DE MEDIADOR
// ─────────────────────────────────────────────────────────────────────────────

object HonorariosMediador {
    fun escalaFamilia(baseJus: Double): Double = when {
        baseJus <= 0.0   -> 3.0
        baseJus <= 30.0  -> 1.0
        baseJus <= 100.0 -> 2.0
        baseJus <= 150.0 -> 3.0
        baseJus <= 300.0 -> 4.0
        else             -> 5.0
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// PANTALLA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun PantallaCalculadoraHonorarios(navController: NavController) {
    val scope = rememberCoroutineScope()

    val tabs = listOf("Mediador Ley 13151", "Abogado Ley 6767", "Abogado Ley 27423", "Mediador PROMESA")
    val pagerState = rememberPagerState(pageCount = { tabs.size })

    Column(modifier = Modifier.fillMaxSize()) {
        UsageCounterIndicator()
        ScrollableTabRow(
            selectedTabIndex = pagerState.currentPage,
            containerColor = colorScheme.surface,
            edgePadding = 16.dp,
            indicator = {}, // Eliminamos el indicador de línea para el diseño framed
            divider = {},   // Eliminamos el divisor para un aspecto más limpio
            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
        ) {
            tabs.forEachIndexed { index, title ->
                val selected = pagerState.currentPage == index
                Tab(
                    selected = selected,
                    onClick = { scope.launch { pagerState.animateScrollToPage(index) } },
                    unselectedContentColor = colorScheme.onSurfaceVariant,
                    selectedContentColor = colorScheme.primary
                ) {
                    Surface(
                        modifier = Modifier.padding(vertical = 8.dp, horizontal = 4.dp),
                        shape = RoundedCornerShape(16.dp),
                        color = if (selected) colorScheme.primaryContainer else colorScheme.surface,
                        border = if (selected) null else BorderStroke(1.dp, colorScheme.outlineVariant.copy(alpha = 0.6f))
                    ) {
                        Text(
                            text = title,
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = if (selected) FontWeight.Bold else FontWeight.Medium,
                            color = if (selected) colorScheme.onPrimaryContainer else colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }

        HorizontalPager(state = pagerState, modifier = Modifier.weight(1f)) { page ->
            when (page) {
                0 -> {
                    Box(modifier = Modifier.fillMaxSize()) {
                        ContenidoHonorariosMediador(
                            navController = navController,
                            scope         = scope
                        )
                        OnboardingTooltip(
                            key      = "onboarding_honorarios_mediador",
                            mensaje  = "Calculá honorarios de mediación según Ley 13151. Podés validar el resultado consultando con nuestra IA.",
                            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
                        )
                    }
                }
                1 -> {
                    Box(modifier = Modifier.fillMaxSize()) {
                        ContenidoHonorariosUnificados6767(navController, scope)
                        OnboardingTooltip(
                            key      = "onboarding_honorarios_6767_unificado",
                            mensaje  = "Buscador inteligente de la Ley 6767. Escribí el nombre del proceso para obtener honorarios judiciales o extrajudiciales.",
                            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
                        )
                    }
                }
                2 -> {
                    Box(modifier = Modifier.fillMaxSize()) {
                        ContenidoLey27423(navController, scope)
                        OnboardingTooltip(
                            key      = "onboarding_honorarios_27423",
                            mensaje  = "Calculadora Federal Ley 27.423. Buscá por nombre de proceso o tags para obtener honorarios en UMA.",
                            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
                        )
                    }
                }
                3 -> {
                    Box(modifier = Modifier.fillMaxSize()) {
                        ContenidoPromesa(
                            navController = navController,
                            scope = scope,
                            onNavigateToAgenda = { navController.navigate(Routes.AGENDA_PROMESA) }
                        )
                        OnboardingTooltip(
                            key      = "onboarding_honorarios_promesa",
                            mensaje  = "Calculadora PROMESA (Salud). Honorarios de mediación exclusiva para salud (Decreto 379/2025).",
                            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
                        )
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Mediador Ley 13151
// ─────────────────────────────────────────────────────────────────────────────

@OptIn(ExperimentalResourceApi::class)
@Composable
fun ContenidoHonorariosMediador(
    navController: NavController,
    scope:         CoroutineScope
) {
    var normativas by remember { mutableStateOf<NormativasRoot?>(null) }
    
    LaunchedEffect(Unit) {
        try {
            val bytes = Res.readBytes("files/normativas_honorarios_mediacion.json")
            val jsonString = bytes.decodeToString()
            normativas = Json.decodeFromString<NormativasRoot>(jsonString)
        } catch (e: Exception) {
            e.printStackTrace()
        }
    }

    var tipoMediacion              by remember { mutableStateOf<String?>(null) }
    var tipoContenido              by remember { mutableStateOf<String?>(null) }
    var montoReclamoStr            by remember { mutableStateOf("") }
    var montoAcuerdoStr            by remember { mutableStateOf("") }
    var valorJusStr                by remember { mutableStateOf(VALOR_JUS_ESTANDAR) }
    var esAlimentos                by remember { mutableStateOf(false) }
    var cantidadReuniones          by remember { mutableFloatStateOf(1f) }
    var comediadorSolicitadoPorPartes by remember { mutableStateOf(false) }
    var resultadoAcuerdo           by remember { mutableStateOf<String?>(null) }
    var huboDesistimiento          by remember { mutableStateOf(false) }
    var desistioAntesReunion       by remember { mutableStateOf<Boolean?>(null) }
    var tasaAportesCajaAlta        by remember { mutableStateOf(false) }
    var sinMontoDeclarado          by remember { mutableStateOf(false) }

    var resultadoGratuita          by remember { mutableStateOf<String?>(null) }
    var acuerdoConContenidoEconomico by remember { mutableStateOf<Boolean?>(null) }
    var montoGratuitaStr           by remember { mutableStateOf("") }
    var esAlimentosGratuita        by remember { mutableStateOf(false) }

    val scrollState = rememberScrollState()
    var resultadoVisibleConDelay by remember { mutableStateOf(false) }
    val thousandSeparator = ThousandSeparatorVisualTransformation()

    var calculoEjecutado           by remember { mutableStateOf(false) }
    var resultadoFinal             by remember { mutableStateOf<ResultadoMediacion?>(null) }

    fun limpiar() {
        tipoMediacion = null; tipoContenido = null; montoReclamoStr = ""; montoAcuerdoStr = ""
        esAlimentos = false
        cantidadReuniones = 1f; comediadorSolicitadoPorPartes = false
        resultadoAcuerdo = null; huboDesistimiento = false
        desistioAntesReunion = null; tasaAportesCajaAlta = false; sinMontoDeclarado = false
        resultadoGratuita = null; acuerdoConContenidoEconomico = null
        montoGratuitaStr = ""; esAlimentosGratuita = false
        resultadoVisibleConDelay = false; calculoEjecutado = false
        resultadoFinal = null
    }

    val necesitaMontoReclamo = tipoContenido == "Económico" || tipoContenido == "Mixto"
    val necesitaMontoAcuerdo = resultadoAcuerdo == "con_monto"

    // 1. Validar el Reclamo Original (Solo exigirlo si realmente está visible)
    val reclamoVisible = necesitaMontoReclamo && resultadoAcuerdo != "con_monto"
    val reclamoValido = if (reclamoVisible) {
        montoReclamoStr.isNotBlank() || sinMontoDeclarado
    } else {
        true // Si está oculto por la UI, pasa la validación automáticamente
    }

    // 2. Validar el Acuerdo Final (Solo exigirlo si se seleccionó "con_monto")
    val acuerdoValido = if (resultadoAcuerdo == "con_monto") {
        montoAcuerdoStr.isNotBlank()
    } else {
        true
    }

    // 3. Validación Final Combinada para habilitar el botón
    val listoParaCalcular = tipoContenido != null && 
        (resultadoAcuerdo != null || huboDesistimiento) && 
        reclamoValido && 
        acuerdoValido

    fun ejecutarCalculo() {
        val n = normativas?.ley13151 ?: return
        val vJus = valorJusStr.toDoubleOrNull() ?: VALOR_JUS_ESTANDAR.toDouble()
        
        val montoReclamoPesos = if (sinMontoDeclarado) 0.0 else montoReclamoStr.toDoubleOrNull() ?: 0.0
        val montoAcuerdoPesos = montoAcuerdoStr.toDoubleOrNull() ?: 0.0
        
        // JERARQUÍA ARANCELARIA (Art. 30 Dec. 184/19):
        // 1. Si hay acuerdo con monto, rige el acuerdo (pero no perfora el piso de la escala).
        // 2. Si el acuerdo es "sin monto", rige el reclamo original como base imponible.
        // 3. Si no hay monto en ninguno, rige el mínimo por "Monto Indeterminado".
        val valorEfectivoPesos = if (resultadoAcuerdo == "con_monto" && montoAcuerdoPesos > 0) {
            montoAcuerdoPesos
        } else {
            montoReclamoPesos
        }
        
        val montoJusCalc = if (vJus > 0 && valorEfectivoPesos > 0) valorEfectivoPesos / vJus else 0.0
        val baseJusEscala = if (esAlimentos && !sinMontoDeclarado) montoJusCalc * n.reglas.alimentos.multiplicadorMeses else montoJusCalc
        val esMixto = tipoContenido == "Mixto"

        val materiaKey = if (tipoMediacion == "Familia") "familia" else "civil_comercial"
        val materia = n.materias[materiaKey] ?: n.materias["familia"]!!

        val hBase = when {
            resultadoAcuerdo == "no_asistio" || resultadoAcuerdo == "imposibilidad" -> n.reglas.inasistencia.honorarioFijoAsignado
            
            // Si es monto indeterminado (Checkbox marcado o ambos montos en 0)
            (sinMontoDeclarado || (valorEfectivoPesos == 0.0 && !necesitaMontoReclamo)) -> {
                materia.minimoSinMonto
            }
            
            // Si hay una base económica (acuerdo o requerimiento), aplicamos la escala por tramos
            baseJusEscala > 0 -> {
                var h = 0.0
                for (tramo in materia.escala) {
                    if (baseJusEscala <= tramo.hasta) {
                        h = tramo.honorario
                        break
                    }
                }
                if (h == 0.0) h = materia.escala.last().honorario
                
                // Retornar directamente el honorario determinado por la escala
                h
            }
            
            else -> materia.minimoSinMonto
        }

        val pMixto = if (esMixto && resultadoAcuerdo != "no_asistio") {
            minOf(materia.plusMixtoBase, materia.tope - hBase).coerceAtLeast(0.0)
        } else 0.0

        val pReuniones = if (tipoMediacion == "Familia" && cantidadReuniones > n.reglas.reuniones.umbralExceso) {
            (cantidadReuniones.toInt() - n.reglas.reuniones.umbralExceso) * n.reglas.reuniones.valorPorReunionExcedente
        } else 0.0

        val honorarioFinalJus = hBase + pMixto
        val subTotal = honorarioFinalJus + pReuniones
        
        val tasaAportes = if (tasaAportesCajaAlta) n.reglas.aportes.tasaAlta else n.reglas.aportes.tasaEstandar
        val aportesCaja = subTotal * tasaAportes
        val subConAportes = subTotal + aportesCaja
        
        val pComediador = if (comediadorSolicitadoPorPartes) subConAportes * (n.reglas.comediador.incrementoFactor - 1.0) else 0.0
        val totalJusPreDesistimiento = subConAportes + pComediador
        
        val factorDesistimiento = if (huboDesistimiento) {
            if (desistioAntesReunion == true) n.reglas.desistimiento.antesNotificacionFactor else n.reglas.desistimiento.despuesNotificacionFactor
        } else 1.0
        
        var totalJusEfectivo = totalJusPreDesistimiento * factorDesistimiento
        if (huboDesistimiento && desistioAntesReunion == true) {
            totalJusEfectivo = maxOf(totalJusEfectivo, n.reglas.desistimiento.antesNotificacionMinimoJus)
        }
        
        resultadoFinal = ResultadoMediacion(
            honorarioBase = hBase,
            plusMixto = pMixto,
            plusReuniones = pReuniones,
            aportesCaja = aportesCaja,
            plusComediador = pComediador,
            totalJus = totalJusEfectivo,
            totalPesos = totalJusEfectivo * vJus,
            factorAjuste = if (totalJusPreDesistimiento > 0) totalJusEfectivo / totalJusPreDesistimiento else 1.0,
            normativa = when {
                resultadoAcuerdo == "no_asistio" -> "Art. 30 y 31, Anexo I - Dec. 184/19"
                huboDesistimiento -> "Art. 30 ap. IV, Anexo I - Dec. 184/19"
                tipoContenido == "Mixto" -> "Aplica máximo ${materia.tope} JUS Decreto 4688/2014"
                else -> "Art. 31 (Honorarios) y Art. 27 (Aportes/Gastos) del Anexo I - Dec. 184/19"
            },
            labelResultado = when {
                huboDesistimiento -> "Desistimiento"
                resultadoAcuerdo == "con_monto"    -> "Acuerdo con monto"
                resultadoAcuerdo == "sin_monto"    -> "Acuerdo sin monto"
                resultadoAcuerdo == "sin_acuerdo"  -> if (sinMontoDeclarado) "Sin acuerdo (Monto Indeterminado)" else "Sin acuerdo"
                resultadoAcuerdo == "no_asistio"   -> "No asistió requerido"
                resultadoAcuerdo == "imposibilidad" -> "Imposibilidad de notificar"
                else -> if (sinMontoDeclarado) "Monto Indeterminado" else "—"
            },
            baseJusEscala = baseJusEscala,
            tasaAportes = tasaAportes,
            esMixto = esMixto,
            valorJus = vJus
        )
        calculoEjecutado = true
        resultadoVisibleConDelay = true
    }

    // --- LÓGICA DE NUDGE (GUÍA ORGÁNICA) ---
    val sutilSpring = spring<Float>(dampingRatio = Spring.DampingRatioLowBouncy, stiffness = Spring.StiffnessLow)

    LaunchedEffect(tipoMediacion) {
        if (tipoMediacion != null) {
            delay(600)
            scrollState.animateScrollTo(150, animationSpec = sutilSpring)
        }
    }

    LaunchedEffect(tipoContenido) {
        if (tipoContenido != null) {
            delay(600)
            scrollState.animateScrollTo(400, animationSpec = sutilSpring)
        }
    }

    LaunchedEffect(resultadoAcuerdo) {
        if (resultadoAcuerdo != null) {
            delay(600)
            scrollState.animateScrollTo(700, animationSpec = sutilSpring)
        }
    }

    LaunchedEffect(huboDesistimiento) {
        if (huboDesistimiento) {
            delay(400)
            scrollState.animateScrollTo(
                (scrollState.value + 250).coerceAtMost(scrollState.maxValue),
                animationSpec = sutilSpring
            )
        }
    }

    val montoGratuitaBase = montoGratuitaStr.toDoubleOrNull() ?: 0.0
    val montoGratuitaPesos = if (esAlimentosGratuita) montoGratuitaBase * 24 else montoGratuitaBase
    val vJusGratuita = valorJusStr.toDoubleOrNull() ?: VALOR_JUS_ESTANDAR.toDouble()
    val montoGratuitaJus = if (vJusGratuita > 0 && montoGratuitaPesos > 0) montoGratuitaPesos / vJusGratuita else 0.0
    val hBaseGratuita = HonorariosMediador.escalaFamilia(montoGratuitaJus)
    val aportesCajaGratuita = hBaseGratuita * 0.13
    val totalGratuitaJus = hBaseGratuita + aportesCajaGratuita
    val totalGratuitaPesos = totalGratuitaJus * vJusGratuita

    WebScrollableBox(scrollState = scrollState, modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.fillMaxSize().imePadding().padding(16.dp).verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
        Text("Calculadora Mediadores Ley 13151", style = MaterialTheme.typography.titleLarge, color = colorScheme.primary, textAlign = TextAlign.Center, fontWeight = FontWeight.Bold)
        HorizontalDivider()

        SeccionLabelHonorarios("1. Tipo de Mediación")
        Column(modifier = Modifier.fillMaxWidth().resaltadoGuia(tipoMediacion == null), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                listOf("Familia", "Civil y Comercial").forEach { tipo ->
                    Button(
                        onClick  = { limpiar(); tipoMediacion = tipo },
                        colors   = ButtonDefaults.buttonColors(containerColor = if (tipoMediacion == tipo) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (tipoMediacion == tipo) colorScheme.onPrimary else colorScheme.onSurface),
                        modifier = Modifier.padding(horizontal = 8.dp)
                    ) { Text(tipo) }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                Button(
                    onClick  = { limpiar(); tipoMediacion = "Gratuita" },
                    colors   = ButtonDefaults.buttonColors(containerColor = if (tipoMediacion == "Gratuita") colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (tipoMediacion == "Gratuita") colorScheme.onPrimary else colorScheme.onSurface),
                    modifier = Modifier.padding(horizontal = 8.dp)
                ) { Text("Mediación Gratuita") }
            }
        }

        if (tipoMediacion != "Gratuita") {
            Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(14.dp), colors = CardDefaults.cardColors(containerColor = colorScheme.surfaceVariant.copy(alpha = 0.35f)), border = BorderStroke(1.dp, colorScheme.outlineVariant.copy(alpha = 0.45f))) {
                Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 12.dp), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.Top) {
                    Icon(imageVector = Icons.Default.Info, contentDescription = null, tint = colorScheme.primary.copy(alpha = 0.8f), modifier = Modifier.padding(top = 2.dp))
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(text = "Gastos administrativos al inicio a abonar por el Requirente: 0,10 jus de gastos administrativos, más el costo de cada notificación", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.SemiBold, color = colorScheme.onSurface)
                        Text(text = "Art. 8, inc. b), Anexo I, Decreto 1747/2011.", style = MaterialTheme.typography.bodySmall, color = colorScheme.onSurfaceVariant)
                    }
                }
            }
        } else {
            CardInfoGratuitaGastosAdministrativos()
        }

        if (tipoMediacion == "Familia" || tipoMediacion == "Civil y Comercial") {
            SeccionLabelHonorarios("2. Contenido")
            Row(Modifier.fillMaxWidth().resaltadoGuia(tipoContenido == null), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                listOf("Económico", "Extrapatrimonial", "Mixto").forEach { opt ->
                    Button(onClick = { tipoContenido = opt; calculoEjecutado = false }, colors = ButtonDefaults.buttonColors(containerColor = if (tipoContenido == opt) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (tipoContenido == opt) colorScheme.onPrimary else colorScheme.onSurface), modifier = Modifier.weight(1f), shape = RoundedCornerShape(8.dp)) { Text(opt, style = MaterialTheme.typography.labelSmall) }
                }
            }

            if (tipoContenido != null) {
                SeccionLabelHonorarios("3. Resultado del acuerdo")
                Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.resaltadoGuia(resultadoAcuerdo == null && !huboDesistimiento)) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        listOf("con_monto" to "Acuerdo c/ monto", "sin_monto" to "Acuerdo s/ monto", "sin_acuerdo" to "Sin acuerdo").forEach { (valor, label) ->
                            Button(onClick = { resultadoAcuerdo = valor; huboDesistimiento = false; calculoEjecutado = false }, colors = ButtonDefaults.buttonColors(containerColor = if (resultadoAcuerdo == valor) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (resultadoAcuerdo == valor) colorScheme.onPrimary else colorScheme.onSurface), modifier = Modifier.weight(1f), shape = RoundedCornerShape(8.dp)) { Text(label, style = MaterialTheme.typography.labelSmall, textAlign = TextAlign.Center) }
                        }
                    }
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        listOf("no_asistio" to "No asistió", "imposibilidad" to "Imp. notificar").forEach { (valor, label) ->
                            Button(onClick = { resultadoAcuerdo = valor; huboDesistimiento = false; calculoEjecutado = false }, colors = ButtonDefaults.buttonColors(containerColor = if (resultadoAcuerdo == valor) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (resultadoAcuerdo == valor) colorScheme.onPrimary else colorScheme.onSurface), modifier = Modifier.weight(1f), shape = RoundedCornerShape(8.dp)) { Text(label, style = MaterialTheme.typography.labelSmall, textAlign = TextAlign.Center) }
                        }
                    }
                }

                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                    Switch(checked = huboDesistimiento, onCheckedChange = {
                        huboDesistimiento = it
                        calculoEjecutado = false
                        if (it) {
                            resultadoAcuerdo = null
                            desistioAntesReunion = null
                        }
                    })
                    Spacer(Modifier.width(8.dp))
                    Column {
                        Text("Desistimiento del Requirente", fontWeight = FontWeight.Bold)
                        Text("Decreto 184/19 - Anexo I, art. 30, ap. IV.", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant)
                    }
                }
                if (huboDesistimiento) {
                    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.errorContainer.copy(alpha = 0.3f))) {
                        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text("¿Cuándo se produjo el desistimiento?", style = MaterialTheme.typography.labelLarge)
                            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Button(
                                    onClick = { desistioAntesReunion = true; calculoEjecutado = false },
                                    colors = ButtonDefaults.buttonColors(containerColor = if (desistioAntesReunion == true) colorScheme.error else colorScheme.surfaceVariant),
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Text("Antes de notificar primera reunión\n(50%, mín. 1 JUS)", style = MaterialTheme.typography.labelSmall, textAlign = TextAlign.Center)
                                }
                                Button(
                                    onClick = { desistioAntesReunion = false; calculoEjecutado = false },
                                    colors = ButtonDefaults.buttonColors(containerColor = if (desistioAntesReunion == false) colorScheme.error else colorScheme.surfaceVariant),
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Text("Posterior a la notificación\n(100%)", style = MaterialTheme.typography.labelSmall, textAlign = TextAlign.Center)
                                }
                            }
                        }
                    }
                }

                // 1. Checkbox Alimentos independiente
                AnimatedVisibility(visible = tipoMediacion == "Familia" || tipoMediacion == "Gratuita") {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.padding(horizontal = 4.dp, vertical = 4.dp)
                    ) {
                        Checkbox(
                            checked = esAlimentos,
                            onCheckedChange = { 
                                esAlimentos = it
                                calculoEjecutado = false 
                            }
                        )
                        Text("¿Es cuota de alimentos? (Aplica x24)")
                    }
                }

                // 2. Reclamo Original: Se muestra SOLO si el acuerdo NO es con monto
                AnimatedVisibility(visible = necesitaMontoReclamo && resultadoAcuerdo != "con_monto") {
                    Column {
                        SeccionLabelHonorarios("4. Monto del Reclamo Original")
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            colors = CardDefaults.cardColors(containerColor = colorScheme.surfaceVariant.copy(alpha = 0.5f))
                        ) {
                            Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                // Selector reactivo de "Sin monto / no declarado"
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { 
                                            sinMontoDeclarado = !sinMontoDeclarado
                                            if (sinMontoDeclarado) montoReclamoStr = ""
                                            calculoEjecutado = false
                                        }
                                ) {
                                    Switch(
                                        checked = sinMontoDeclarado,
                                        onCheckedChange = { 
                                            sinMontoDeclarado = it
                                            if (it) montoReclamoStr = ""
                                            calculoEjecutado = false
                                        }
                                    )
                                    Spacer(Modifier.width(12.dp))
                                    Text(
                                        text = "Sin monto / no declarado", 
                                        style = MaterialTheme.typography.bodyMedium,
                                        fontWeight = FontWeight.Bold
                                    )
                                }

                                // Campo de texto para monto: desaparece si se marca "Sin monto"
                                AnimatedVisibility(
                                    visible = !sinMontoDeclarado,
                                    enter = fadeIn() + expandVertically(),
                                    exit = fadeOut() + shrinkVertically()
                                ) {
                                    OutlinedTextField(
                                        value = montoReclamoStr,
                                        onValueChange = { 
                                            montoReclamoStr = it.filter { c -> c.isDigit() }
                                            calculoEjecutado = false 
                                        },
                                        label = { Text(if (esAlimentos) "Cuota Mensual Reclamada" else "Monto del Reclamo Original") },
                                        leadingIcon = { Text("$") },
                                        singleLine = true,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(top = 8.dp)
                                            .resaltadoGuia(montoReclamoStr.isEmpty() && !sinMontoDeclarado),
                                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                        visualTransformation = thousandSeparator
                                    )
                                }
                            }
                        }
                    }
                }

                // 3. Acuerdo Final: Se muestra SOLO si el resultado es "con_monto"
                AnimatedVisibility(visible = resultadoAcuerdo == "con_monto") {
                    Column {
                        SeccionLabelHonorarios("5. Monto del Acuerdo Final")
                        Card(modifier = Modifier.fillMaxWidth().resaltadoGuia(montoAcuerdoStr.isEmpty()), colors = CardDefaults.cardColors(containerColor = colorScheme.primaryContainer.copy(alpha = 0.2f))) {
                            Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(
                                    value = montoAcuerdoStr,
                                    onValueChange = { 
                                        montoAcuerdoStr = it.filter { c -> c.isDigit() }
                                        calculoEjecutado = false 
                                    },
                                    label = { Text(if (esAlimentos) "Valor de Cuota Mensual Acordada" else "Monto final del acuerdo") },
                                    leadingIcon = { Text("$") },
                                    singleLine = true,
                                    modifier = Modifier.fillMaxWidth(),
                                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                    visualTransformation = thousandSeparator
                                )
                            }
                        }
                    }
                }

                if (tipoMediacion == "Familia") {
                    Column(modifier = Modifier.padding(vertical = 8.dp)) {
                        Text("Reuniones realizadas: ${cantidadReuniones.toInt()}", fontWeight = FontWeight.Bold)
                        Slider(value = cantidadReuniones, onValueChange = { cantidadReuniones = it; calculoEjecutado = false }, valueRange = 1f..5f, steps = 3)
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) { (1..5).forEach { Text(it.toString(), style = MaterialTheme.typography.labelSmall) } }
                    }
                }

                Row(verticalAlignment = Alignment.CenterVertically) { Switch(checked = comediadorSolicitadoPorPartes, onCheckedChange = { comediadorSolicitadoPorPartes = it; calculoEjecutado = false }); Spacer(Modifier.width(8.dp)); Text("Comediador pedido por las partes (+1/3)") }
                HorizontalDivider()

                OutlinedTextField(value = valorJusStr, onValueChange = { valorJusStr = it.filter { c -> c.isDigit() }; calculoEjecutado = false }, label = { Text("Valor actual de 1 JUS") }, leadingIcon = { Text("$") }, singleLine = true, modifier = Modifier.fillMaxWidth(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number), visualTransformation = thousandSeparator)

                SeccionLabelHonorarios("5. Aportes Caja")
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    listOf(false to "13%", true to "20,4%").forEach { (esAlta, label) ->
                        Button(onClick = { tasaAportesCajaAlta = esAlta; calculoEjecutado = false }, colors = ButtonDefaults.buttonColors(containerColor = if (tasaAportesCajaAlta == esAlta) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (tasaAportesCajaAlta == esAlta) colorScheme.onPrimary else colorScheme.onSurface), modifier = Modifier.weight(1f)) { Text(label) }
                    }
                }

                Spacer(Modifier.height(12.dp))

                CalculatorActionLock(
                    navController = navController,
                    onAction = { ejecutarCalculo() }
                ) { onClick ->
                    Button(
                        onClick = onClick,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = listoParaCalcular,
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Icon(Icons.Default.Calculate, null)
                        Spacer(Modifier.width(8.dp))
                        Text("Calcular Honorarios Mediación", fontWeight = FontWeight.Bold)
                    }
                }
                Spacer(modifier = Modifier.height(40.dp))

                // --- RESULTADO CON DELAY ---
                AnimatedVisibility(visible = resultadoVisibleConDelay && calculoEjecutado) {
                    resultadoFinal?.let { res ->
                        val textoCopia = """
                            RESUMEN HONORARIOS MEDIACIÓN - LEY 13.151
                            Resultado: ${res.labelResultado}
                            Honorario Base: ${(res.honorarioBase + res.plusMixto).toFormato(2, true)} JUS
                            Gastos Admin: ${res.plusReuniones.toFormato(2, true)} JUS
                            Aportes Caja (${if (tasaAportesCajaAlta) "20,4%" else "13%"}): ${res.aportesCaja.toFormato(2, true)} JUS
                            ${if (comediadorSolicitadoPorPartes) "Comediador (1/3): ${res.plusComediador.toFormato(2, true)} JUS" else ""}
                            -------------------------------------------
                            TOTAL JUS: ${res.totalJus.toFormato(2, true)} JUS
                            TOTAL PESOS: $ ${res.totalPesos.toFormato(2, true)}
                            (Valor JUS: $ ${res.valorJus.toFormato(2, true)})
                            -------------------------------------------
                            Calculado con www.juridicorosario.com
                        """.trimIndent()

                        CajaResultadoAccionable(
                            titulo = "Cálculo Estimado Ley 13.151",
                            textoParaCompartir = textoCopia
                        ) {
                            ResultRow("Resultado:", res.labelResultado)
                            ResultRow("Honorario:", "${(res.honorarioBase + res.plusMixto).toFormato(2, true)} JUS")
                            if (res.plusReuniones > 0) ResultRow("Gastos Admin:", "${res.plusReuniones.toFormato(2, true)} JUS")
                            ResultRow("Aportes Caja (${if (tasaAportesCajaAlta) "20,4%" else "13%"}):", "${res.aportesCaja.toFormato(2, true)} JUS")
                            if (comediadorSolicitadoPorPartes) ResultRow("Comediador (1/3):", "${res.plusComediador.toFormato(2, true)} JUS")
                            HorizontalDivider(Modifier.padding(vertical = 4.dp))
                            ResultRow("TOTAL JUS:", "${res.totalJus.toFormato(2, true)} JUS", isBold = true)
                            ResultRow("TOTAL PESOS:", "$ ${res.totalPesos.toFormato(2, true)}", isBold = true, color = colorScheme.primary)

                            if (resultadoAcuerdo == "sin_monto" && montoReclamoStr.isNotEmpty()) {
                                Text(
                                    text = "Base de cálculo: Monto del Reclamo Original (Art. 30 Dec. 184/19).",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = colorScheme.primary,
                                    modifier = Modifier.padding(top = 4.dp),
                                    fontWeight = FontWeight.Bold
                                )
                            }

                            Text(
                                text = "Los montos acordados por las partes rigen como base, con el piso del mínimo legal de la ley arancelaria.",
                                style = MaterialTheme.typography.bodySmall,
                                color = colorScheme.secondary,
                                modifier = Modifier.padding(top = 8.dp),
                                fontWeight = FontWeight.SemiBold
                            )

                            Text(
                                text = "Normativa aplicable: ${res.normativa}",
                                style = MaterialTheme.typography.labelSmall,
                                color = colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(top = 4.dp)
                            )

                            if (huboDesistimiento && desistioAntesReunion != null) {
                                Text(
                                    text = "Decreto 184/19 - Anexo I, art. 30, ap. IV.",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = colorScheme.secondary,
                                    modifier = Modifier.padding(top = 4.dp),
                                    fontWeight = FontWeight.Bold
                                )
                            }

                            if (resultadoAcuerdo == "no_asistio") {
                                val totalHipotetico = totalHipoteticoPesos(res.valorJus, res.baseJusEscala, tipoMediacion, necesitaMontoReclamo || necesitaMontoAcuerdo, cantidadReuniones, res.tasaAportes, comediadorSolicitadoPorPartes, res.esMixto)
                                val saldoAReclamarPesos = (totalHipotetico - res.valorJus).coerceAtLeast(0.0)
                                val multaPesos = 2.0 * res.valorJus

                                HorizontalDivider(Modifier.padding(vertical = 8.dp))
                                Text("⚠️ Inasistencia del Requerido (Decreto 184/19)", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = colorScheme.error)
                                Spacer(Modifier.height(4.dp))
                                Text("• El Requirente abona: 1 JUS (Art. 30 II).", style = MaterialTheme.typography.bodySmall)
                                Text("• Requerido carga con el saldo: $ ${saldoAReclamarPesos.toFormato(2, true)}.", style = MaterialTheme.typography.bodySmall)
                                Text("• Multa por inasistencia: $ ${multaPesos.toFormato(2, true)} (2 JUS - Art. 30 III).", style = MaterialTheme.typography.bodySmall)
                                Text("Total a reclamar al Requerido vía apremio: $ ${(saldoAReclamarPesos + multaPesos).toFormato(2, true)}.", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
                                Text("Base legal: Anexo I - Decreto 184/19, Art. 15, 30 y 31.", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant, modifier = Modifier.padding(top = 4.dp))
                            }
                        }
                    }
                }

                if (calculoEjecutado && resultadoFinal != null) {
                    val res = resultadoFinal!!
                    CalculatorActionLock(
                        navController = navController,
                        onAction = {
                            MediacionGastoViewModel.instance.setInitialData(
                                honorariosJus = res.honorarioBase + res.plusMixto,
                                honorariosPesos = (res.honorarioBase + res.plusMixto) * res.valorJus,
                                aportesJus = res.aportesCaja,
                                aportesPesos = res.aportesCaja * res.valorJus,
                                gastosAdminJus = res.plusReuniones,
                                gastosAdminPesos = res.plusReuniones * res.valorJus,
                                valorJus = res.valorJus,
                                tipoMediacion = tipoMediacion ?: ""
                            )
                            navController.navigate(Routes.PLANILLA_MEDIACION)
                        }
                    ) { onClick ->
                        Button(
                            onClick  = onClick,
                            modifier = Modifier.fillMaxWidth(),
                            enabled  = calculoEjecutado
                        ) {
                            Text("Planilla gastos cierre mediación")
                        }
                    }
                }
            }
        }

        if (tipoMediacion == "Gratuita") {
            ContenidoMediacionGratuita(
                navController = navController,
                scope = scope, vJus = valorJusStr.toDoubleOrNull() ?: VALOR_JUS_ESTANDAR.toDouble(), valorJusStr = valorJusStr, onValorJusChange = { valorJusStr = it }, thousandSeparator = thousandSeparator,
                resultadoGratuita = resultadoGratuita, onResultadoChange = { resultadoGratuita = it; acuerdoConContenidoEconomico = null; montoGratuitaStr = ""; esAlimentosGratuita = false },
                acuerdoConContenidoEconomico = acuerdoConContenidoEconomico, onAcuerdoEconomicoChange = { acuerdoConContenidoEconomico = it; montoGratuitaStr = "" },
                montoGratuitaStr = montoGratuitaStr, onMontoGratuitaChange = { montoGratuitaStr = it }, esAlimentosGratuita = esAlimentosGratuita, onEsAlimentosChange = { esAlimentosGratuita = it },
                gratuittaDebeConvertirse = (tipoMediacion == "Gratuita" && resultadoGratuita == "con_acuerdo" && acuerdoConContenidoEconomico == true), hBaseGratuita = hBaseGratuita, aportesCajaGratuita = aportesCajaGratuita, totalGratuitaJus = hBaseGratuita + aportesCajaGratuita, totalGratuitaPesos = (hBaseGratuita + aportesCajaGratuita) * vJusGratuita,
                textoHonorariosMediador = normativas?.ley13151?.textoInformativoGeneral ?: "",
                onCargandoChange = { /* ignore */ }
            )
        }
    }
}
}

private fun totalHipoteticoPesos(vJus: Double, baseJus: Double, tipo: String?, necesitaMonto: Boolean, reuniones: Float, tasa: Double, comediador: Boolean, esMixto: Boolean): Double {
    val h = if (necesitaMonto && baseJus > 0) {
        if (baseJus <= 30) 1.0 else if (baseJus <= 100) 2.0 else if (baseJus <= 150) 3.0 else if (baseJus <= 300) 4.0 else 5.0
    } else {
        if (tipo == "Familia") 3.0 else 2.0
    }
    val pmBase = if (tipo == "Familia") 3.0 else 2.0
    val pm = if (esMixto) minOf(pmBase, 5.0 - h).coerceAtLeast(0.0) else 0.0
    val r = if (reuniones > 3) (reuniones.toInt() - 3) * 0.10 else 0.0
    val sub = (h + pm + r) * (1 + tasa)
    return (if (comediador) sub * 1.333 else sub) * vJus
}

@Composable
fun ContenidoMediacionGratuita(
    navController: NavController,
    scope: CoroutineScope, vJus: Double, valorJusStr: String, onValorJusChange: (String) -> Unit, thousandSeparator: VisualTransformation,
    resultadoGratuita: String?, onResultadoChange: (String) -> Unit, acuerdoConContenidoEconomico: Boolean?, onAcuerdoEconomicoChange: (Boolean) -> Unit,
    montoGratuitaStr: String, onMontoGratuitaChange: (String) -> Unit, esAlimentosGratuita: Boolean, onEsAlimentosChange: (Boolean) -> Unit,
    gratuittaDebeConvertirse: Boolean, hBaseGratuita: Double, aportesCajaGratuita: Double, totalGratuitaJus: Double, totalGratuitaPesos: Double,
    textoHonorariosMediador: String,
    onCargandoChange: (Boolean) -> Unit
) {
    val normativaGratuita = if (resultadoGratuita == "con_acuerdo" && acuerdoConContenidoEconomico == true) {
        "Cese de gratuidad: Art. 18 Dec. 1747/11"
    } else {
        "Art. 18 Dec. 1747/11"
    }

    SeccionLabelHonorarios("2. Resultado de la Mediación")
    Row(modifier = Modifier.fillMaxWidth().resaltadoGuia(resultadoGratuita == null), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        listOf("sin_acuerdo" to "Sin Acuerdo", "con_acuerdo" to "Con Acuerdo").forEach { (v, l) -> Button(onClick = { onResultadoChange(v) }, colors = ButtonDefaults.buttonColors(containerColor = if (resultadoGratuita == v) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (resultadoGratuita == v) colorScheme.onPrimary else colorScheme.onSurface), modifier = Modifier.weight(1f), shape = RoundedCornerShape(8.dp)) { Text(l) } }
    }

    if (resultadoGratuita == "sin_acuerdo") {
        Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.secondaryContainer.copy(alpha = 0.6f))) { Row(modifier = Modifier.padding(16.dp), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Default.CheckCircle, null, tint = colorScheme.secondary); Column { Text("Mantiene gratuidad", fontWeight = FontWeight.Bold); Text("Al no haber acuerdo, no corresponde pago.", style = MaterialTheme.typography.bodySmall); Text("Normativa: $normativaGratuita", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant) } } }
    }

    if (resultadoGratuita == "con_acuerdo") {
        SeccionLabelHonorarios("3. ¿Tiene contenido económico?")
        Row(modifier = Modifier.fillMaxWidth().resaltadoGuia(acuerdoConContenidoEconomico == null), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            listOf(true to "Sí", false to "No").forEach { (v, l) -> Button(onClick = { onAcuerdoEconomicoChange(v) }, colors = ButtonDefaults.buttonColors(containerColor = if (acuerdoConContenidoEconomico == v) colorScheme.primary else colorScheme.surfaceVariant, contentColor = if (acuerdoConContenidoEconomico == v) colorScheme.onPrimary else colorScheme.onSurface), modifier = Modifier.weight(1f), shape = RoundedCornerShape(8.dp)) { Text(l) } }
        }

        if (acuerdoConContenidoEconomico == false) {
            Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.secondaryContainer.copy(alpha = 0.6f))) { Row(modifier = Modifier.padding(16.dp), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Default.CheckCircle, null, tint = colorScheme.secondary); Column { Text("Mantiene gratuidad", fontWeight = FontWeight.Bold); Text("Acuerdo sin monto. No corresponde regulación.", style = MaterialTheme.typography.bodySmall); Text("Normativa: $normativaGratuita", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant) } } }
        }

        if (acuerdoConContenidoEconomico == true) {
            Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.errorContainer.copy(alpha = 0.35f)), border = BorderStroke(1.dp, colorScheme.error.copy(alpha = 0.40f))) { Row(modifier = Modifier.padding(14.dp), horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Default.Warning, null, tint = colorScheme.error); Column { Text("Gratuidad cesa", fontWeight = FontWeight.Bold, color = colorScheme.error); Text("Corresponde regulación de honorarios.", style = MaterialTheme.typography.bodySmall) } } }
            SeccionLabelHonorarios("Valor del JUS")
            OutlinedTextField(value = valorJusStr, onValueChange = { onValorJusChange(it.filter { c -> c.isDigit() }) }, label = { Text("$") }, modifier = Modifier.fillMaxWidth(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number), visualTransformation = thousandSeparator)
            SeccionLabelHonorarios("4. Monto del acuerdo")
            Card(modifier = Modifier.fillMaxWidth().resaltadoGuia(montoGratuitaStr.isEmpty()), colors = CardDefaults.cardColors(containerColor = colorScheme.surfaceVariant.copy(alpha = 0.5f))) {
                Column(modifier = Modifier.padding(12.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().clickable { onEsAlimentosChange(!esAlimentosGratuita) }) { Checkbox(checked = esAlimentosGratuita, onCheckedChange = { onEsAlimentosChange(it) }); Column { Text("Es cuota de alimentos", fontWeight = FontWeight.Bold); Text("Aplica x24", style = MaterialTheme.typography.labelSmall) } }
                    OutlinedTextField(
                        value = montoGratuitaStr,
                        onValueChange = { onMontoGratuitaChange(it.filter { c -> c.isDigit() }) },
                        label = { Text(if (esAlimentosGratuita) "Cuota Mensual Final del Acuerdo" else "Monto Final del Acuerdo") },
                        modifier = Modifier.fillMaxWidth(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        visualTransformation = thousandSeparator
                    )
                }
            }

            if (montoGratuitaStr.isNotEmpty()) {
                val textoCopia = """
                    RESUMEN MEDIACIÓN GRATUITA - LEY 13.151
                    Resultado: Acuerdo con Contenido Económico
                    Honorario Base: ${hBaseGratuita.toFormato(2)} JUS
                    Aportes Caja: ${aportesCajaGratuita.toFormato(2)} JUS
                    -------------------------------------------
                    TOTAL PESOS: $ ${totalGratuitaPesos.toFormato(2, true)}
                    (Valor JUS: $ ${vJus.toFormato(2, true)})
                    -------------------------------------------
                    Calculado con www.juridicorosario.com
                """.trimIndent()

                CajaResultadoAccionable(
                    titulo = "Cálculo",
                    textoParaCompartir = textoCopia
                ) {
                    ResultRow("Base:", "${hBaseGratuita.toFormato(2)} JUS")
                    ResultRow("Aportes:", "${aportesCajaGratuita.toFormato(2)} JUS")
                    HorizontalDivider(Modifier.padding(vertical = 4.dp))
                    ResultRow("TOTAL:", "$ ${totalGratuitaPesos.toFormato(2, true)}", isBold = true)
                    Text(
                        text = "Normativa aplicable: $normativaGratuita",
                        style = MaterialTheme.typography.labelSmall,
                        color = colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(top = 4.dp)
                    )
                }

                Spacer(Modifier.height(12.dp))

                CalculatorActionLock(
                    navController = navController,
                    onAction = {
                        MediacionGastoViewModel.instance.setInitialData(
                            honorariosJus = hBaseGratuita,
                            honorariosPesos = hBaseGratuita * vJus,
                            aportesJus = aportesCajaGratuita,
                            aportesPesos = aportesCajaGratuita * vJus,
                            gastosAdminJus = 0.0,
                            gastosAdminPesos = 0.0,
                            valorJus = vJus,
                            tipoMediacion = "Gratuita"
                        )
                        navController.navigate(Routes.PLANILLA_MEDIACION)
                    }
                ) { onClick ->
                    Button(
                        onClick = onClick,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("Planilla gastos cierre mediación")
                    }
                }
            }
        }
    }
}
// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Honorarios Unificados Ley 6767 (Refactorizado para Web con LazyColumn)
// ─────────────────────────────────────────────────────────────────────────────

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
fun ContenidoHonorariosUnificados6767(
    navController: NavController,
    scope: CoroutineScope
) {
    var searchQuery by remember { mutableStateOf("") }
    var expandirDropdown by remember { mutableStateOf(false) }

    var historialJus by remember { mutableStateOf<List<ValorJusHistorico>>(emptyList()) }
    var expandirMenuJus by remember { mutableStateOf(false) }

    var leyData by remember { mutableStateOf<Ley6767Data?>(null) }
    var procesosList by remember { mutableStateOf<List<TipoProceso>>(emptyList()) }
    var actuacionesList by remember { mutableStateOf<List<ActuacionExtrajudicial>>(emptyList()) }
    var estaCargandoBaseDeDatos by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        try {
            val bytes = Res.readBytes("files/ley_6767.json")
            val texto = bytes.decodeToString()
            val data = Json {
                ignoreUnknownKeys = true
                coerceInputValues = true
                isLenient = true
                explicitNulls = false
            }.decodeFromString<Ley6767Data>(texto)
            leyData = data

            procesosList = data.procesosJudiciales.map { p ->
                TipoProceso(
                    nombre = p.titulo,
                    clave = p.id,
                    etapas = if (p.multiplicadorBase != null || p.reduccionEscala < 1.0 || p.articulo == "6") Ley6767.etapasCompletas else Ley6767.etapaUnica,
                    reduccion = p.reduccionEscala,
                    esMinimo = p.minimoJus > 0,
                    minimoJus = p.minimoJus,
                    maximoJus = p.maximoJus,
                    isRange = p.maximoJus != null || p.articulo == "6" || p.articulo == "23",
                    fuero = p.fuero,
                    multiplicadorBase = p.multiplicadorBase,
                    nota = p.descripcionBase ?: "",
                    requiereMonto = p.requiereMonto,
                    codigosSelladoSugeridos = p.codigosSelladoSugeridos,
                    articulo = p.articulo,
                    inciso = p.inciso,
                    permiteQuerellante = p.permiteQuerellante,
                    sinonimos = p.sinonimos,
                    competencia = p.competencia,
                    viaIngreso = p.viaIngreso,
                    mediacion = p.mediacion
                )
            }

            actuacionesList = data.actuacionesExtrajudiciales.map { a ->
                ActuacionExtrajudicial(
                    nombre = a.titulo,
                    clave = a.id,
                    tipo = if (a.porcentajeAlicuota != null || a.reduccionEscala < 1.0) TipoRegulacionExtrajudicial.PORCENTAJE_CON_MINIMO else TipoRegulacionExtrajudicial.FIJO_JUS,
                    minimoJus = a.minimoJus,
                    mensaje = "Art. ${a.articulo}${if (a.inciso != null) " inc. ${a.inciso}" else ""} Ley 6767. ${a.descripcionBase ?: ""}",
                    porcentaje = a.porcentajeAlicuota ?: a.reduccionEscala,
                    codigosSelladoSugeridos = a.codigosSelladoSugeridos,
                    articulo = a.articulo,
                    inciso = a.inciso,
                    requiereMonto = a.requiereMonto,
                    sinonimos = a.sinonimos,
                    competencia = a.competencia,
                    viaIngreso = a.viaIngreso,
                    mediacion = a.mediacion
                )
            }
        } catch (e: Exception) {
            e.printStackTrace()
            println("Error crítico cargando ley_6767.json: ${e.message}")
        } finally {
            estaCargandoBaseDeDatos = false
        }
    }

    LaunchedEffect(Unit) {
        try {
            val bytes = Res.readBytes("files/valor_jus_base.txt")
            val texto = bytes.decodeToString()
            val lineas = texto.lines()
                .filter { it.contains("$") && it.contains("a partir de", ignoreCase = true) }
                .mapNotNull { linea ->
                    try {
                        val partes = linea.split("a partir de", ignoreCase = true)
                        val valorPart = partes[0].filter { it.isDigit() || it == ',' || it == '.' }.trim()
                        val fechaPart = partes[1].split("–", ";", "Resolución", "Acuerdo").first().trim().removePrefix("l").trim()
                        if (valorPart.isNotEmpty() && fechaPart.isNotEmpty()) {
                            ValorJusHistorico(fechaPart, valorPart.replace(".", "").replace(",", "."))
                        } else null
                    } catch (e: Exception) { null }
                }
            historialJus = lineas
        } catch (e: Exception) { e.printStackTrace() }
    }

    var procesoSeleccionado by remember { mutableStateOf<TipoProceso?>(null) }
    var actuacionSeleccionada by remember { mutableStateOf<ActuacionExtrajudicial?>(null) }

    var etapaSeleccionada by remember { mutableStateOf<Etapa?>(null) }
    var montoBaseStr by remember { mutableStateOf("") }
    var valorJusStr by remember { mutableStateOf(VALOR_JUS_ESTANDAR) }
    var conQuerellante by remember { mutableStateOf(false) }

    var esCalculoObsoleto by remember { mutableStateOf(false) }

    val selladosSugeridosItems = remember { mutableStateListOf<SelladoItem>() }
    val selectedSelladosMap = remember { mutableStateMapOf<String, Boolean>() }

    LaunchedEffect(procesoSeleccionado, actuacionSeleccionada) {
        selladosSugeridosItems.clear()
        selectedSelladosMap.clear()
        val codigos = (procesoSeleccionado?.codigosSelladoSugeridos ?: emptyList()) +
                (actuacionSeleccionada?.codigosSelladoSugeridos ?: emptyList())

        if (codigos.isNotEmpty()) {
            val todos = SelladosRepository.getItems()
            codigos.forEach { cod ->
                todos.find { it.codigo == cod }?.let { item ->
                    selladosSugeridosItems.add(item)
                    selectedSelladosMap[item.codigo] = false
                }
            }
        }
    }

    var resultadoJudicial by remember { mutableStateOf<ResultadoHonorario?>(null) }
    var resultadoExtrajudicial by remember { mutableStateOf<ResultadoExtrajudicial?>(null) }

    // Este es el único manejador de scroll de esta pestaña, totalmente independiente
    val lazyListState = rememberLazyListState()
    val thousandSeparator = ThousandSeparatorVisualTransformation()

    val allItems = remember(procesosList, actuacionesList) { procesosList + actuacionesList }

    val filteredItems = remember(searchQuery, allItems) {
        if (searchQuery.isEmpty()) allItems else {
            val q = searchQuery.normalized().trim()
            allItems.filter { item ->
                val (nombre, sinonimos) = when (item) {
                    is TipoProceso -> item.nombre to item.sinonimos
                    is ActuacionExtrajudicial -> item.nombre to item.sinonimos
                    else -> "" to emptyList()
                }
                val nombreNorm = nombre.normalized()
                nombreNorm.contains(q) || sinonimos.any { it.normalized().contains(q) }
            }
        }
    }

    var resultadoVisibleConDelay by remember { mutableStateOf(false) }

        LaunchedEffect(resultadoJudicial, resultadoExtrajudicial) {
            if (resultadoJudicial != null || resultadoExtrajudicial != null) {
                delay(400)
                resultadoVisibleConDelay = true
                delay(800)
                lazyListState.animateScrollToItem(0, scrollOffset = 550)
            } else {
                resultadoVisibleConDelay = false
            }
        }

    // ─────────────────────────────────────────────────────────────────────────
    // CONTENEDOR PRINCIPAL: LAZY COLUMN BLINDADO PARA WEB
    // ─────────────────────────────────────────────────────────────────────────
    WebLazyColumnScrollbar(state = lazyListState, modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            state = lazyListState,
            modifier = Modifier.fillMaxSize().imePadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(16.dp)
    ) {
        // ITEM 1: Todo el bloque de formularios, buscador y resultados originales
        item {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text("Honorarios Ley 6767", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = colorScheme.primary)

                Column(modifier = Modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = searchQuery,
                        onValueChange = { newValue ->
                            searchQuery = newValue
                            expandirDropdown = newValue.isNotEmpty()

                            if (procesoSeleccionado != null || actuacionSeleccionada != null) {
                                procesoSeleccionado = null
                                actuacionSeleccionada = null
                                resultadoJudicial = null
                                resultadoExtrajudicial = null
                                montoBaseStr = ""
                            }
                        },
                        label = { Text("Buscar proceso o gestión...") },
                        modifier = Modifier.fillMaxWidth(),
                        trailingIcon = { Icon(Icons.Default.Search, null) },
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp)
                    )

                    AnimatedVisibility(
                        visible = expandirDropdown && searchQuery.isNotEmpty(),
                        enter = expandVertically(),
                        exit = shrinkVertically()
                    ) {
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 300.dp)
                                .padding(top = 4.dp),
                            shape = RoundedCornerShape(12.dp),
                            colors = CardDefaults.cardColors(containerColor = colorScheme.surface),
                            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
                            border = BorderStroke(1.dp, colorScheme.outlineVariant)
                        ) {
                            val resultados = filteredItems
                            if (resultados.isEmpty()) {
                                Box(Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                                    Text(
                                        if (estaCargandoBaseDeDatos) "Cargando base de datos..." else "No se encontraron resultados for '$searchQuery'",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = colorScheme.onSurfaceVariant,
                                        textAlign = TextAlign.Center
                                    )
                                }
                            } else {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .verticalScroll(rememberScrollState())
                                ) {
                                    resultados.take(20).forEach { item ->
                                        val (nombre, esJudicial) = when (item) {
                                            is TipoProceso -> item.nombre to true
                                            is ActuacionExtrajudicial -> item.nombre to false
                                            else -> "" to false
                                        }
                                        Column(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable {
                                                    searchQuery = nombre
                                                    expandirDropdown = false
                                                    if (esJudicial) {
                                                        val p = item as TipoProceso
                                                        procesoSeleccionado = p
                                                        actuacionSeleccionada = null
                                                        etapaSeleccionada = p.etapas.firstOrNull()
                                                    } else {
                                                        actuacionSeleccionada = item as ActuacionExtrajudicial
                                                        procesoSeleccionado = null
                                                    }
                                                    resultadoJudicial = null
                                                    resultadoExtrajudicial = null
                                                    montoBaseStr = ""
                                                    esCalculoObsoleto = false
                                                }
                                                .padding(horizontal = 16.dp, vertical = 12.dp)
                                        ) {
                                            Text(nombre, fontWeight = FontWeight.SemiBold, color = colorScheme.onSurface)
                                            Text(if (esJudicial) "Proceso Judicial" else "Actuación Extrajudicial", style = MaterialTheme.typography.labelSmall, color = colorScheme.secondary)
                                        }
                                        HorizontalDivider(color = colorScheme.surfaceVariant.copy(alpha = 0.5f))
                                    }
                                }
                            }
                        }
                    }
                }

                Box(modifier = Modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = valorJusStr,
                        onValueChange = { valorJusStr = it.filter { c -> c.isDigit() }; esCalculoObsoleto = true },
                        label = { Text("Valor del JUS ($)") },
                        modifier = Modifier.fillMaxWidth(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        visualTransformation = thousandSeparator,
                        leadingIcon = { Icon(Icons.Default.MonetizationOn, null, tint = Gold400) },
                        trailingIcon = {
                            IconButton(onClick = { expandirMenuJus = true }) {
                                Icon(Icons.Default.History, "Historial JUS")
                            }
                        }
                    )

                    DropdownMenu(
                        expanded = expandirMenuJus,
                        onDismissRequest = { expandirMenuJus = false },
                        modifier = Modifier.fillMaxWidth(0.9f).heightIn(max = 300.dp)
                    ) {
                        historialJus.forEach { item ->
                            DropdownMenuItem(
                                text = {
                                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                        Text(item.fecha, style = MaterialTheme.typography.bodySmall)
                                        Text("$ ${item.valor}", fontWeight = FontWeight.Bold, color = colorScheme.primary)
                                    }
                                },
                                onClick = {
                                    valorJusStr = item.valor.split(".").first()
                                    esCalculoObsoleto = true
                                    expandirMenuJus = false
                                }
                            )
                        }
                    }
                }

                procesoSeleccionado?.let { p ->
                    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.primaryContainer.copy(alpha = 0.3f))) {
                        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text("Selección: ${p.nombre}", fontWeight = FontWeight.Bold, color = colorScheme.primary)
                            Text("Fundamento: Art. ${p.articulo}${if (p.inciso != null) ", inc. ${p.inciso}" else ""} - Ley 6767", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                            if (p.nota.isNotEmpty()) Text(p.nota, style = MaterialTheme.typography.labelSmall)
                        }
                    }

                    AnimatedVisibility(visible = p.permiteQuerellante) {
                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                            Switch(checked = conQuerellante, onCheckedChange = { conQuerellante = it; esCalculoObsoleto = true })
                            Spacer(Modifier.width(8.dp))
                            Column {
                                Text("Actuación como Querellante", fontWeight = FontWeight.Bold)
                                Text("Incrementa honorarios en un 50% (Art. 14)", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant)
                            }
                        }
                    }

                    if (p.etapas.size > 1) {
                        SeccionLabelHonorarios("Etapa del Proceso")
                        Row(Modifier.fillMaxWidth(), Arrangement.spacedBy(8.dp)) {
                            p.etapas.forEach { e ->
                                val sel = etapaSeleccionada?.clave == e.clave
                                Button(
                                    onClick = { etapaSeleccionada = e; esCalculoObsoleto = true },
                                    modifier = Modifier.weight(1f),
                                    shape = RoundedCornerShape(8.dp),
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = if (sel) colorScheme.primary else colorScheme.surfaceVariant,
                                        contentColor = if (sel) colorScheme.onPrimary else colorScheme.onSurface
                                    )
                                ) {
                                    Text(e.nombre, style = MaterialTheme.typography.labelSmall)
                                }
                            }
                        }
                    }

                    AnimatedVisibility(visible = p.requiereMonto) {
                        Column(modifier = Modifier.fillMaxWidth()) {
                            SeccionLabelHonorarios(if (p.multiplicadorBase != null) "Cuantía (Base)" else "Monto del Juicio / Base Económica")
                            OutlinedTextField(
                                value = montoBaseStr,
                                onValueChange = { montoBaseStr = it.filter { c -> c.isDigit() }; esCalculoObsoleto = true },
                                label = {
                                    Text(
                                        if (p.multiplicadorBase != null)
                                            "Monto cuota/canon (Se calculará x${p.multiplicadorBase.toInt()})"
                                        else "Monto del Juicio ($)"
                                    )
                                },
                                modifier = Modifier.fillMaxWidth().resaltadoGuia(montoBaseStr.isEmpty()),
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                visualTransformation = thousandSeparator
                            )

                            val montoD = montoBaseStr.toDoubleOrNull() ?: 0.0
                            if (p.multiplicadorBase != null && montoD > 0) {
                                Text(
                                    text = "Base imponible considerada: $ ${(montoD * p.multiplicadorBase).toFormato(2, true)}",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = colorScheme.secondary,
                                    modifier = Modifier.padding(top = 4.dp, start = 4.dp)
                                )
                            }
                        }
                    }

                    CalculatorActionLock(
                        navController = navController,
                        onAction = {
                            resultadoJudicial = Ley6767.calcular(
                                p, etapaSeleccionada!!,
                                montoBaseStr.toDoubleOrNull() ?: 0.0,
                                valorJusStr.toDoubleOrNull() ?: VALOR_JUS_ESTANDAR.toDouble(),
                                conQuerellante
                            )
                            esCalculoObsoleto = false
                        }
                    ) { onClick ->
                        Button(
                            onClick = onClick,
                            modifier = Modifier.fillMaxWidth(),
                            enabled = p.esMinimo || montoBaseStr.isNotEmpty() || !p.requiereMonto
                        ) {
                            Text("Calcular Honorarios Judiciales")
                        }
                    }
                    Spacer(modifier = Modifier.height(20.dp))
                }

                actuacionSeleccionada?.let { act ->
                    val requiereMontoAct = act.requiereMonto

                    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.secondaryContainer.copy(alpha = 0.3f))) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text("Gestión: ${act.nombre}", fontWeight = FontWeight.Bold, color = colorScheme.secondary)
                            Text("Fundamento: Art. ${act.articulo}${if (act.inciso != null) ", inc. ${act.inciso}" else ""} - Ley 6767", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                        }
                    }

                    AnimatedVisibility(visible = requiereMontoAct) {
                        Column(modifier = Modifier.fillMaxWidth()) {
                            val label = if (act.clave == "traslado") "Cantidad de Kilómetros" else "Monto del Acto ($)"
                            OutlinedTextField(
                                value = montoBaseStr,
                                onValueChange = { montoBaseStr = it.filter { c -> c.isDigit() }; esCalculoObsoleto = true },
                                label = { Text(label) },
                                modifier = Modifier.fillMaxWidth().resaltadoGuia(montoBaseStr.isEmpty()),
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                visualTransformation = if (act.clave == "traslado") VisualTransformation.None else thousandSeparator
                            )
                        }
                    }

                    CalculatorActionLock(
                        navController = navController,
                        onAction = {
                            resultadoExtrajudicial = Ley6767Extrajudicial.calcular(
                                act,
                                montoBaseStr.toDoubleOrNull() ?: 0.0,
                                valorJusStr.toDoubleOrNull() ?: VALOR_JUS_ESTANDAR.toDouble()
                            )
                            esCalculoObsoleto = false
                        }
                    ) { onClick ->
                        Button(
                            onClick = onClick,
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !act.requiereMonto || montoBaseStr.isNotEmpty()
                        ) {
                            Text("Calcular Honorarios Extrajudiciales")
                        }
                    }
                    Spacer(modifier = Modifier.height(20.dp))
                }

                AnimatedVisibility(visible = resultadoVisibleConDelay) {
                    Column(
                        modifier = Modifier.fillMaxWidth().graphicsLayer { alpha = if (esCalculoObsoleto) 0.5f else 1.0f },
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        resultadoJudicial?.let { res ->
                            val montoD = montoBaseStr.toDoubleOrNull() ?: 0.0
                            val baseParaSellados = if (procesoSeleccionado?.multiplicadorBase != null)
                                montoD * procesoSeleccionado!!.multiplicadorBase!!
                            else montoD

                            val selladosCalculados = selladosSugeridosItems.map { item ->
                                val detalle = SelladosEngine.realizarCalculoIntegral(item, baseParaSellados)
                                item to detalle
                            }
                            val totalGastosSellados = selladosCalculados
                                .filter { selectedSelladosMap[it.first.codigo] == true }
                                .sumOf { it.second.total }

                            val aportes = res.minimoPesos * 0.13
                            val netoAbogado = res.minimoPesos - aportes
                            val totalConAportes = res.minimoPesos + aportes
                            val totalGeneralPresupuesto = totalConAportes + totalGastosSellados

                            val textoCopia = buildString {
                                append("RESUMEN HONORARIOS JUDICIALES - LEY 6767\n")
                                append("Proceso: ${res.descripcion}\n")
                                append("Honorario Base / Regulación: $ ${res.minimoPesos.toFormato(2, true)} (${res.minimoJus.toFormato(2)} JUS)\n")
                                append("Aportes (13% Caja Forense): $ ${aportes.toFormato(2, true)}\n")
                                append("🔹 Neto Abogado (Bruto - Aportes): $ ${netoAbogado.toFormato(2, true)}\n")
                                append("🔸 Total a Cobrar/Presupuestar (Bruto + Aportes): $ ${totalConAportes.toFormato(2, true)}\n")

                                val activos = selladosCalculados.filter { selectedSelladosMap[it.first.codigo] == true }
                                if (activos.isNotEmpty()) {
                                    append("\nGASTOS E IMPUESTOS:\n")
                                    activos.forEach { (item, det) ->
                                        append("- ${item.nombre}: $ ${det.total.toFormato(2, true)}\n")
                                    }
                                    append("\nTOTAL CON GASTOS: $ ${totalGeneralPresupuesto.toFormato(2, true)}\n")
                                }

                                append("${if (res.maximoJus > res.minimoJus) "Escala hasta: ${res.maximoJus.toFormato(2)} JUS ($ ${res.maximoPesos.toFormato(2, true)})\n" else ""}")
                                append("Fundamento: ${res.articulo}\n")
                                append("-------------------------------------------\n")
                                append("Calculado con www.juridicorosario.com")
                            }

                            CajaResultadoAccionable(
                                titulo = "PRESUPUESTO LEY 6767",
                                textoParaCompartir = textoCopia
                            ) {
                                Text("Desglose de honorarios y aportes de ley.", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSecondaryContainer.copy(alpha = 0.7f))

                                Spacer(Modifier.height(8.dp))
                                ResultRow("Honorario Base / Regulación:", "$ ${res.minimoPesos.toFormato(2, true)}", isBold = true)
                                ResultRow("Aportes (13% Caja Forense):", "$ ${aportes.toFormato(2, true)}")

                                HorizontalDivider(Modifier.padding(vertical = 4.dp))

                                ResultRow("🔹 Neto Abogado (Bruto - Aportes):", "$ ${netoAbogado.toFormato(2, true)}", isBold = true, color = colorScheme.primary)
                                ResultRow("🔸 Total a Cobrar/Presupuestar:", "$ ${totalConAportes.toFormato(2, true)}", isBold = true, color = colorScheme.secondary)

                                if (selladosSugeridosItems.isNotEmpty()) {
                                    Spacer(Modifier.height(12.dp))
                                    Text("Gastos e Impuestos Sugeridos", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = colorScheme.secondary)
                                    FlowRow(
                                        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                                        verticalArrangement = Arrangement.spacedBy(4.dp)
                                    ) {
                                        selladosCalculados.forEach { (item, det) ->
                                            val isSelected = selectedSelladosMap[item.codigo] ?: false
                                            FilterChip(
                                                selected = isSelected,
                                                onClick = { selectedSelladosMap[item.codigo] = !isSelected },
                                                label = { Text(text = "Sellado: ${item.nombre} ($ ${det.total.toFormato(0)})", maxLines = 1, overflow = TextOverflow.Ellipsis, style = MaterialTheme.typography.labelSmall) },
                                                leadingIcon = if (isSelected) { { Icon(Icons.Default.Check, null, Modifier.size(16.dp)) } } else null
                                            )
                                        }
                                    }
                                }

                                HorizontalDivider(Modifier.padding(vertical = 4.dp))
                                ResultRow("🔸 TOTAL CON GASTOS:", "$ ${totalGeneralPresupuesto.toFormato(2, true)}", isBold = true)

                                HorizontalDivider(Modifier.padding(vertical = 4.dp))
                                Text("Fundamento Legal y Escala:", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                                ResultRow("Mínimo en JUS:", "${res.minimoJus.toFormato(2)} JUS")
                                Text(res.articulo, style = MaterialTheme.typography.bodySmall)

                                if (res.maximoJus > res.minimoJus) {
                                    Card(
                                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                                        colors = CardDefaults.cardColors(containerColor = colorScheme.surface.copy(alpha = 0.5f)),
                                        border = BorderStroke(1.dp, colorScheme.outlineVariant)
                                    ) {
                                        SelectionContainer {
                                            Column(modifier = Modifier.padding(8.dp)) {
                                                Text("Posible escala hasta el máximo legal: ${res.maximoJus.toFormato(2)} JUS", style = MaterialTheme.typography.labelSmall)
                                                Text("Equivalente a: $ ${res.maximoPesos.toFormato(2, true)}", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        resultadoExtrajudicial?.let { res ->
                            val montoD = montoBaseStr.toDoubleOrNull() ?: 0.0
                            val selladosCalculados = selladosSugeridosItems.map { item ->
                                val detalle = SelladosEngine.realizarCalculoIntegral(item, montoD)
                                item to detalle
                            }
                            val totalGastosSellados = selladosCalculados
                                .filter { selectedSelladosMap[it.first.codigo] == true }
                                .sumOf { it.second.total }

                            val aportes = res.honorarioPesos * 0.13
                            val netoAbogado = res.honorarioPesos - aportes
                            val totalConAportes = res.honorarioPesos + aportes
                            val totalGeneralPresupuesto = totalConAportes + totalGastosSellados

                            val textoCopia = buildString {
                                append("RESUMEN HONORARIOS EXTRAJUDICIALES - LEY 6767\n")
                                append("Gestión: ${actuacionSeleccionada?.nombre ?: ""}\n")
                                append("Honorario Base / Sugerido: $ ${res.honorarioPesos.toFormato(2, true)} (${res.honorarioJus.toFormato(2)} JUS)\n")
                                append("Aportes (13% Caja Forense): $ ${aportes.toFormato(2, true)}\n")
                                append("🔹 Neto Abogado (Bruto - Aportes): $ ${netoAbogado.toFormato(2, true)}\n")
                                append("🔸 Total a Cobrar/Presupuestar (Bruto + Aportes): $ ${totalConAportes.toFormato(2, true)}\n")

                                val activos = selladosCalculados.filter { selectedSelladosMap[it.first.codigo] == true }
                                if (activos.isNotEmpty()) {
                                    append("\nGASTOS E IMPUESTOS:\n")
                                    activos.forEach { (item, det) ->
                                        append("- ${item.nombre}: $ ${det.total.toFormato(2, true)}\n")
                                    }
                                    append("\nTOTAL CON GASTOS: $ ${totalGeneralPresupuesto.toFormato(2, true)}\n")
                                }

                                append("Fundamento: ${res.mensaje}\n")
                                append("-------------------------------------------\n")
                                append("Calculado con www.juridicorosario.com")
                            }

                            CajaResultadoAccionable(
                                titulo = "PRESUPUESTO EXTRAJUDICIAL",
                                textoParaCompartir = textoCopia
                            ) {
                                Text("Desglose de honorarios y aportes de ley.", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSecondaryContainer.copy(alpha = 0.7f))

                                Spacer(Modifier.height(8.dp))
                                ResultRow("Honorario Base / Sugerido:", "$ ${res.honorarioPesos.toFormato(2, true)}", isBold = true)
                                ResultRow("Aportes (13% Caja Forense):", "$ ${aportes.toFormato(2, true)}")

                                HorizontalDivider(Modifier.padding(vertical = 4.dp))

                                ResultRow("🔹 Neto Abogado (Bruto - Aportes):", "$ ${netoAbogado.toFormato(2, true)}", isBold = true, color = colorScheme.primary)
                                ResultRow("🔸 Total a Cobrar/Presupuestar:", "$ ${totalConAportes.toFormato(2, true)}", isBold = true, color = colorScheme.secondary)

                                if (selladosSugeridosItems.isNotEmpty()) {
                                    Spacer(Modifier.height(12.dp))
                                    Text("Gastos e Impuestos Sugeridos", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = colorScheme.secondary)
                                    FlowRow(
                                        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                                        verticalArrangement = Arrangement.spacedBy(4.dp)
                                    ) {
                                        selladosCalculados.forEach { (item, det) ->
                                            val isSelected = selectedSelladosMap[item.codigo] ?: false
                                            FilterChip(
                                                selected = isSelected,
                                                onClick = { selectedSelladosMap[item.codigo] = !isSelected },
                                                label = { Text(text = "Sellado: ${item.nombre} ($ ${det.total.toFormato(0)})", maxLines = 1, overflow = TextOverflow.Ellipsis, style = MaterialTheme.typography.labelSmall) },
                                                leadingIcon = if (isSelected) { { Icon(Icons.Default.Check, null, Modifier.size(16.dp)) } } else null
                                            )
                                        }
                                    }
                                }

                                HorizontalDivider(Modifier.padding(vertical = 4.dp))
                                ResultRow("🔸 TOTAL CON GASTOS:", "$ ${totalGeneralPresupuesto.toFormato(2, true)}", isBold = true)

                                HorizontalDivider(Modifier.padding(vertical = 4.dp))
                                Text("Fundamento Legal:", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                                ResultRow("Equivalente en JUS:", "${res.honorarioJus.toFormato(2)} JUS")
                                Text(res.mensaje, style = MaterialTheme.typography.bodySmall)
                            }
                        }
                    }
                }

                Spacer(Modifier.height(20.dp))
                FichaTecnicaProcesal(
                    proceso = procesoSeleccionado,
                    actuacion = actuacionSeleccionada,
                    valorJus = valorJusStr.toDoubleOrNull() ?: VALOR_JUS_ESTANDAR.toDouble()
                )
            }
        }

        // ITEM 2: Título separador de la lista
        item {
            Spacer(Modifier.height(24.dp))
            HorizontalDivider()
            Text(
                "Listado Completo - Ley 6767",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.ExtraBold,
                color = colorScheme.secondary,
                modifier = Modifier.padding(vertical = 8.dp)
            )
            Text(
                "Explorá todos los procesos y actuaciones contemplados en la normativa.",
                style = MaterialTheme.typography.bodySmall,
                color = colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center
            )
            Spacer(Modifier.height(8.dp))
        }

        // ITERACIÓN PEREZOSA 1: Procesos Judiciales
        items(procesosList) { item ->
            CardLey6767Visual(item = item) { cod ->
                navController.navigate(Routes.CODIGOS_SELLADOS)
            }
        }

        // ITERACIÓN PEREZOSA 2: Actuaciones Extrajudiciales
        items(actuacionesList) { item ->
            CardLey6767Visual(item = item) { cod ->
                navController.navigate(Routes.CODIGOS_SELLADOS)
            }
        }
    }
}
}

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
fun CardLey6767Visual(item: Any, onSugerenciaClick: (String) -> Unit) {
    var expandido by rememberSaveable { mutableStateOf(false) }
    val rotation by animateFloatAsState(if (expandido) 180f else 0f)

    val titulo: String
    val articulo: String
    val inciso: String?
    val nota: String
    val sinonimos: List<String>
    val codigosSellado: List<String>
    val esJudicial: Boolean

    when (item) {
        is TipoProceso -> {
            titulo = item.nombre
            articulo = item.articulo
            inciso = item.inciso
            nota = item.nota
            sinonimos = item.sinonimos
            codigosSellado = item.codigosSelladoSugeridos
            esJudicial = true
        }
        is ActuacionExtrajudicial -> {
            titulo = item.nombre
            articulo = item.articulo
            inciso = item.inciso
            nota = item.mensaje
            sinonimos = item.sinonimos
            codigosSellado = item.codigosSelladoSugeridos
            esJudicial = false
        }
        else -> return
    }

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .clickable { expandido = !expandido },
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = colorScheme.surface),
        border = BorderStroke(1.dp, colorScheme.outlineVariant.copy(alpha = 0.6f))
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    color = if (esJudicial) colorScheme.primaryContainer else colorScheme.secondaryContainer,
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        text = if (esJudicial) "Judicial" else "Extrajudicial",
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = if (esJudicial) colorScheme.onPrimaryContainer else colorScheme.onSecondaryContainer
                    )
                }
                Spacer(Modifier.width(8.dp))
                Text(
                    text = "Art. $articulo${if (inciso != null) " inc. $inciso" else ""}",
                    style = MaterialTheme.typography.labelSmall,
                    color = colorScheme.onSurfaceVariant
                )
                Spacer(modifier = Modifier.weight(1f))
                Icon(
                    Icons.Default.ExpandMore,
                    contentDescription = null,
                    modifier = Modifier.rotate(rotation),
                    tint = colorScheme.primary
                )
            }

            Spacer(Modifier.height(8.dp))
            Text(
                text = titulo,
                fontWeight = FontWeight.Bold,
                style = MaterialTheme.typography.titleMedium,
                color = colorScheme.onSurface
            )

            AnimatedVisibility(visible = expandido) {
                Column(modifier = Modifier.padding(top = 12.dp)) {
                    HorizontalDivider(color = colorScheme.outlineVariant, thickness = 0.5.dp)
                    Spacer(Modifier.height(8.dp))

                    if (nota.isNotEmpty()) {
                        Text(
                            text = nota,
                            style = MaterialTheme.typography.bodySmall,
                            color = colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(bottom = 8.dp)
                        )
                    }

                    if (sinonimos.isNotEmpty()) {
                        Text(
                            text = "Búsqueda: ${sinonimos.joinToString(", ")}",
                            style = MaterialTheme.typography.labelSmall,
                            color = colorScheme.primary.copy(alpha = 0.7f),
                            fontSize = 10.sp
                        )
                    }

                    if (codigosSellado.isNotEmpty()) {
                        Spacer(Modifier.height(12.dp))
                        Text("Sellados sugeridos:", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                        FlowRow(
                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            codigosSellado.forEach { cod ->
                                SuggestionChip(
                                    onClick = { onSugerenciaClick(cod) },
                                    label = { Text("Cód. $cod", fontSize = 10.sp) },
                                    icon = { Icon(Icons.Default.Add, null, Modifier.size(14.dp)) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun FichaTecnicaProcesal(
    proceso: TipoProceso?,
    actuacion: ActuacionExtrajudicial?,
    valorJus: Double
) {
    var expandido by remember { mutableStateOf(false) }

    // Determinamos si hay datos específicos para mostrar
    val competencia = proceso?.competencia ?: actuacion?.competencia
    val viaIngreso = proceso?.viaIngreso ?: actuacion?.viaIngreso
    val mediacion = proceso?.mediacion ?: actuacion?.mediacion

    val tieneDatosExtra = competencia != null || viaIngreso != null || mediacion != null

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (tieneDatosExtra) colorScheme.secondaryContainer.copy(alpha = 0.4f)
            else colorScheme.surfaceVariant.copy(alpha = 0.5f)
        ),
        border = BorderStroke(1.dp, colorScheme.outlineVariant.copy(alpha = 0.5f)),
        onClick = { expandido = !expandido }
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        imageVector = if (tieneDatosExtra) Icons.Default.Description else Icons.Default.TableChart,
                        contentDescription = null,
                        tint = if (tieneDatosExtra) colorScheme.primary else colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.width(12.dp))
                    Text(
                        text = if (tieneDatosExtra) "Ficha Técnica Procesal" else "Escalas de Referencia (Art. 6)",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold
                    )
                }
                Icon(
                    imageVector = if (expandido) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    contentDescription = null
                )
            }

            AnimatedVisibility(visible = expandido) {
                Column(modifier = Modifier.padding(top = 16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    if (tieneDatosExtra) {
                        competencia?.let {
                            FichaItem(Icons.Default.Gavel, "Radicación / Competencia", it)
                        }
                        viaIngreso?.let {
                            FichaItem(Icons.Default.CloudUpload, "Vía de Ingreso", it)
                        }
                        mediacion?.let {
                            FichaItem(Icons.Default.Handshake, "Mediación (Ley 13151)", it)
                        }

                        // Botón de Modelos (Placeholder por ahora)
                        OutlinedButton(
                            onClick = { /* Navegación a modelos o carpeta */ },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(8.dp)
                        ) {
                            Icon(Icons.Default.FolderOpen, null)
                            Spacer(Modifier.width(8.dp))
                            Text("Ver Modelos de Escritos", style = MaterialTheme.typography.labelMedium)
                        }

                        HorizontalDivider(modifier = Modifier.padding(vertical = 4.dp), color = colorScheme.outlineVariant)
                    }

                    // Siempre mostramos las escalas al final como referencia técnica
                    TablaReferenciaLey6767(valorJus)
                }
            }
        }
    }
}

@Composable
fun FichaItem(icon: ImageVector, label: String, value: String) {
    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(18.dp).padding(top = 2.dp),
            tint = colorScheme.primary
        )
        Spacer(Modifier.width(12.dp))
        Column {
            Text(label, style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTES AUXILIARES INDEPENDIENTES
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun SeccionLabelHonorarios(texto: String) {
    Text(texto, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = colorScheme.primary, modifier = Modifier.padding(top = 4.dp))
}

@Composable
fun ResultRow(label: String, valor: String, isBold: Boolean = false, color: Color = Color.Unspecified) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, style = MaterialTheme.typography.bodySmall)
        Text(valor, style = MaterialTheme.typography.bodySmall, fontWeight = if (isBold) FontWeight.Bold else FontWeight.Normal, color = if (color != Color.Unspecified) color else colorScheme.onSurface)
    }
}

@Composable
fun TablaReferenciaLey6767(valorJus: Double) {
    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.surface), border = BorderStroke(1.dp, colorScheme.outline)) {
        Column(modifier = Modifier.padding(14.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("Escala Art. 6", fontWeight = FontWeight.Bold, color = colorScheme.primary)
            listOf("Hasta 15 JUS" to "22%–33%", "16–45 JUS" to "20%–26%", "46–90 JUS" to "18%–24%", "91–150 JUS" to "17%–22%", "151–450 JUS" to "15%–20%", "451–750 JUS" to "13%–17%", "751+ JUS" to "10%–13%").forEach { (r, v) -> Row(Modifier.fillMaxWidth(), Arrangement.SpaceBetween) { Text(r, style = MaterialTheme.typography.labelSmall); Text(v, style = MaterialTheme.typography.labelSmall, color = colorScheme.primary) } }
        }
    }
}

@Composable
fun CardInfoGratuitaGastosAdministrativos() {
    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.tertiaryContainer.copy(alpha = 0.4f)), border = BorderStroke(1.dp, colorScheme.tertiary.copy(alpha = 0.35f))) {
        Column(modifier = Modifier.padding(14.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Default.Info, null, tint = colorScheme.tertiary); Text("Gratuidad — Gastos iniciales", fontWeight = FontWeight.Bold, color = colorScheme.onTertiaryContainer) }
            Text("En mediación gratuita el requirente abona 0,10 jus de gastos administrativos y se exime inicialmente de honorarios y boletas, las notificaciones sin cargo se gestionan a través de AGEM. Si hay acuerdo económico, la gratuidad cesa (Art. 18 Dec. 1747/11).", style = MaterialTheme.typography.bodySmall)
        }
    }
}

enum class TipoRegulacionExtrajudicial { FIJO_JUS, PORCENTAJE_CON_MINIMO }

object Ley6767Extrajudicial {
    fun calcular(act: ActuacionExtrajudicial, m: Double, vJ: Double): ResultadoExtrajudicial {
        if (act.tipo == TipoRegulacionExtrajudicial.FIJO_JUS) return ResultadoExtrajudicial(false, act.mensaje, act.minimoJus, act.minimoJus * vJ, null, act.minimoJus, act.minimoJus * vJ)
        val hP = if (act.clave == "cobro_extrajudicial") m * act.porcentaje else m * act.porcentaje
        val fP = maxOf(hP, act.minimoJus * vJ)
        return ResultadoExtrajudicial(true, act.mensaje, fP / vJ, fP, null, act.minimoJus, act.minimoJus * vJ)
    }
}

data class ResultadoExtrajudicial(val requiereMonto: Boolean, val mensaje: String, val honorarioJus: Double, val honorarioPesos: Double, val honorarioPorcentaje: String?, val minimoJus: Double, val minimoPesos: Double)